import { useEffect } from "react";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SWRConfig } from "swr";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Stack } from '@mui/material';
import AppRoutes from "./components/global/AppRoutes";
import { fetcher } from "./utils/fetcher";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TransactionProvider } from "./contexts/TransactionContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 5 * 60 * 1000, // 5 minutes
      // gcTime: 10 * 60 * 1000, // 10 minutes
      // retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  if (event?.query?.state?.error) {
    const error = event.query.state.error;
    console.error('React Query Error:', error);
    Sentry.captureException(error, {
      tags: {
        errorType: 'ReactQuery',
      },
      contexts: {
        reactQuery: {
          queryKey: String(event.query.queryKey),
          error: error?.message || String(error),
        },
      },
    });
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event?.mutation?.state?.status === 'error' && event.mutation.state.error) {
    const error = event.mutation.state.error;
    console.error('React Query Mutation Error:', error);
    Sentry.captureException(error, {
      tags: {
        errorType: 'ReactQueryMutation',
      },
      contexts: {
        reactQuery: {
          error: error?.message || String(error),
        },
      },
    });
  }
});

function App() {
  useEffect(() => {
    const handleResize = () => {
      const rootElement = document.documentElement;
      rootElement.style.setProperty(
        "--screen-height",
        `${window.innerHeight}px`
      );
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={
        <Stack height="var(--screen-height)" justifyContent="center" alignItems="center">
          Đã xảy ra lỗi / Something went wrong
        </Stack>
      }
      onError={(error, errorInfo) => {
        console.error('ErrorBoundary caught an error:', error);
        console.error('Error info:', errorInfo);
        const componentStack = typeof errorInfo === 'object' && 'componentStack' in errorInfo
          ? (errorInfo as { componentStack?: string }).componentStack
          : undefined;
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: componentStack || String(errorInfo),
            },
          },
        });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TransactionProvider>
            <OnboardingProvider>
              <SWRConfig
                value={{
                  fetcher,
                  revalidateOnFocus: false,
                  revalidateOnReconnect: false,
                  dedupingInterval: 10000,
                  errorRetryCount: 3,
                  errorRetryInterval: 5000,
                  focusThrottleInterval: 5000,
                  refreshInterval: 0,
                  onError: (error, key) => {
                    console.error('SWR Error:', error, 'for key:', key);
                    Sentry.captureException(error, {
                      tags: {
                        errorType: 'SWR',
                        swrKey: String(key),
                      },
                      contexts: {
                        swr: {
                          key: String(key),
                          error: error?.message || String(error),
                        },
                      },
                    });
                  },
                }}
              >
                <AppRoutes />
                <ToastContainer />
              </SWRConfig>
            </OnboardingProvider>
          </TransactionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default Sentry.withProfiler(App);
